// PhysicalProfileCard — sidebar card showing latest player_measurements.
//
// Each stat row is skipped when its source value is null. Wingspan is
// the headline metric (hoop-accent color) per the design tokens. If every
// measurement is null we render a small "No measurements yet" placeholder
// rather than an empty card.

interface Measurements {
  height_cm: number | null
  weight_kg: number | null
  wingspan_cm: number | null
  standing_reach_cm: number | null
  max_vert_cm: number | null
  measured_at: string | null
  measured_by: string | null
}

export default function PhysicalProfileCard({
  measurements,
}: {
  measurements: Measurements | null
}) {
  const m = measurements
  const hasAny =
    m &&
    (m.height_cm != null ||
      m.weight_kg != null ||
      m.wingspan_cm != null ||
      m.standing_reach_cm != null ||
      m.max_vert_cm != null)

  // UX #5 — hide the card entirely on the read profile when there's no
  // data, instead of a placeholder. The edit page (MeasurementsForm) still
  // exposes every field regardless.
  if (!hasAny) return null

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-4">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-hoop-accent mb-3">
        Physical Profile
      </h2>

      <dl className="space-y-2">
          {m!.height_cm != null && (
            <Row label="Height" value={`${formatNum(m!.height_cm)} cm`} />
          )}
          {m!.weight_kg != null && (
            <Row label="Weight" value={`${formatNum(m!.weight_kg)} kg`} />
          )}
          {m!.wingspan_cm != null && (
            <Row
              label="Wingspan"
              value={`${formatNum(m!.wingspan_cm)} cm`}
              highlight
            />
          )}
          {m!.standing_reach_cm != null && (
            <Row
              label="Standing reach"
              value={`${formatNum(m!.standing_reach_cm)} cm`}
            />
          )}
          {m!.max_vert_cm != null && (
            <Row
              label="Vertical (max)"
              value={`${formatNum(m!.max_vert_cm)} cm`}
            />
          )}
        </dl>
    </section>
  )
}

function Row({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between text-xs">
      <dt className="text-hoop-text-3">{label}</dt>
      <dd
        className={
          highlight
            ? 'font-semibold text-hoop-accent'
            : 'font-medium text-hoop-text'
        }
      >
        {value}
      </dd>
    </div>
  )
}

function formatNum(n: number): string {
  // Trim trailing zeros; preserve one decimal if non-integer.
  if (Number.isInteger(n)) return String(n)
  return n.toFixed(1)
}
